import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { v4 } from 'uuid';

import {
  addUsersEventColumn,
  changeUsersEvent,
  deleteUsersEventColumn,
  executeUsers,
  executeUsersCsv,
  setUsersEventColumnValue,
  setUsersExecuteId,
} from '../../actions/users';
import {
  getEventNames,
  getExecuteIdsFromJobIds,
  getMetainf,
  getProjectZoneId,
  getUsersColumns,
  getUsersEvent,
  getUsersExecuteId,
  getUsersExecutionEvent,
  isUsersExecuting,
} from '../../reducers/analyticsApp';
import UsersView from '../../components/users/UsersView';
import { getConfig } from '../../utils/table';
import { isValidUsers } from '../../utils/event/common';
import { cancelRequest } from '../../api/execute';
import CsvDownloadButton from '../navigation/csv/CsvDownloadButton';
import CsvRequests from '../navigation/csv/CsvRequests';

const getColumns = ({ columns }) => columns;
const getMetainformation = ({ metainf }) => metainf;

const getAvailableUsersEventFields = createSelector(
  [getColumns, getMetainformation],
  (columns, metainf) => {
    const columnsValues = _(columns)
      .map((c) => c.value)
      .compact()
      .value();

    return {
      meta: _.omit(metainf.meta, columnsValues),
      groups: metainf.groups.map((g) => ({
        ...g,
        fields: g.fields.filter((f) => !columnsValues.includes(f.path)),
      })),
    };
  }
);

const getColumnsData = createSelector([getColumns, getMetainformation], (columns, metainf) => {
  const fields = _(metainf.groups).map('fields').flatten().value();
  return columns.map((c) => ({
    ...c,
    ...fields.find((field) => {
      return field.path === c.value;
    }),
  }));
});

const isExecuteDisabled = (state) => {
  const usersEvent = getUsersEvent(state);
  const usersColumns = getUsersColumns(state);
  return !isValidUsers(usersEvent, usersColumns);
};

const mapStateToProps = (state) => {
  const event = getUsersEvent(state);
  const columns = getUsersColumns(state);
  const metainf = getMetainf(state, event);
  const executeIds = getExecuteIdsFromJobIds(state);
  const executeId = getUsersExecuteId(state);

  return {
    tableConfig: getConfig(
      getUsersExecutionEvent(state),
      getMetainf(state),
      getProjectZoneId(state)
    ),
    metainf: getAvailableUsersEventFields({ columns, metainf }),
    columns: getColumnsData({ columns, metainf }),
    executing: isUsersExecuting(state),
    executeDisabled: isExecuteDisabled(state),
    extraActions: [
      {
        renderElement: (closeMenu) => (
          <CsvDownloadButton
            title="Download CSV"
            clickHandler={executeUsersCsv}
            closeMenu={closeMenu}
          />
        ),
      },
      { renderElement: (closeMenu) => <CsvRequests closeMenu={closeMenu} /> },
    ],
    events: getEventNames(state),
    cancelButtonDisabled: !executeIds.includes(executeId),
    executeId,
    event,
  };
};

const mapDispatchToProps = (dispatch) => ({
  onExecute: () => {
    const executeId = v4();
    dispatch(setUsersExecuteId(executeId));
    return dispatch(executeUsers(executeId));
  },
  onRequestCancel: (executeId) => dispatch(cancelRequest(executeId)),
  setUsersEventColumnValue: (id, value) => dispatch(setUsersEventColumnValue(id, value)),
  deleteUsersEventColumn: (id) => dispatch(deleteUsersEventColumn(id)),
  addUsersEventColumn: () => dispatch(addUsersEventColumn()),
  changeUsersEvent: (event) => dispatch(changeUsersEvent(event)),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps,
  onRequestCancel: () => dispatchProps.onRequestCancel(stateProps.executeId),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(UsersView);
